import React, {useCallback, useState} from 'react'
import {useDispatch} from 'react-redux'
import styled from '@emotion/styled'
import {DatePickerOverlay} from 'components/AvailabilityCalendarPane/DatePickerOverlay'
import {setSelectedAvailabilityDate} from 'modules/search/slice'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Icon} from '@vio/atlas/Icon'
import {getAvailabilityStatus} from '@vio/core/src/_web/availability'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {AvailabilityHotelEntity} from '@vio/core/src/availability/types'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

interface Props {
  hotelId: string
  selectedDate: string
  fetchMonthlyPrices: (Date) => void
  offersData?: SearchOffer[]
  hotelAvailabilityPrices: AvailabilityHotelEntity
  nights: number
  checkIn: string
  sourceComponentPrefix: string
  availabilitySearchComplete: boolean
}

const ButtonLinkWrapper = styled.div`
  white-space: nowrap;
  flex-shrink: 0;
`

export const AvailabilityCalendarTrigger = ({
  hotelId,
  selectedDate,
  fetchMonthlyPrices,
  offersData,
  hotelAvailabilityPrices,
  nights,
  checkIn,
  sourceComponentPrefix,
  availabilitySearchComplete
}: Props) => {
  const dispatch = useDispatch()
  const [isOpenedDatePicker, setIsOpenedDatePicker] = useState<boolean>(false)

  const sourceComponentAvailabilityCalendar = `${sourceComponentPrefix}AvailabilityCalendar`
  const sourceComponentAvailabilityPane = `${sourceComponentPrefix}AvailabilityPane`

  const handleMonthChange = useCallback(
    (date: Date) => {
      fetchMonthlyPrices(date)
    },
    [fetchMonthlyPrices]
  )

  const handleDatePickerClose = useCallback(() => {
    setIsOpenedDatePicker(false)
  }, [])

  const handleSeeMore = useCallback(() => {
    setIsOpenedDatePicker(true)
    fetchMonthlyPrices(new Date(checkIn))
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.AvailabilityPaneSeeMore,
        action: Action.Clicked,
        component: sourceComponentAvailabilityPane
      })
    )
  }, [checkIn, dispatch, fetchMonthlyPrices, sourceComponentAvailabilityPane])

  const handleDayClick = useCallback(
    (date: string) => {
      const price = hotelAvailabilityPrices?.[date]?.cheapestNightlyRate
      if (!price && !availabilitySearchComplete) return
      dispatch(setSelectedAvailabilityDate(date))
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.AvailabilityCalendarDay,
          action: Action.Clicked,
          payload: {
            date,
            availabilityStatus: getAvailabilityStatus({
              hotelAvailabilityPrices,
              date,
              searchComplete: availabilitySearchComplete
            })
          },
          component: sourceComponentAvailabilityCalendar
        })
      )
    },
    [
      dispatch,
      hotelAvailabilityPrices,
      availabilitySearchComplete,
      sourceComponentAvailabilityCalendar
    ]
  )

  return (
    <>
      <ButtonLinkWrapper>
        <ButtonLink
          size="sm"
          isCompact
          iconStart={<Icon name="Calendar" size="sm" />}
          onClick={handleSeeMore}
          dataId="AvailabilityCalendarTrigger"
        >
          <FormattedMessageWrapper
            id="viewCalendar"
            defaultMessage="View calendar"
          />
        </ButtonLink>
      </ButtonLinkWrapper>
      <DatePickerOverlay
        hotelId={hotelId}
        isOpen={isOpenedDatePicker}
        selectedDate={selectedDate}
        onDayClick={handleDayClick}
        onClose={handleDatePickerClose}
        offersData={offersData}
        nights={nights}
        hotelAvailabilityPrices={hotelAvailabilityPrices}
        onMonthChange={handleMonthChange}
        sourceComponent={sourceComponentAvailabilityCalendar}
        checkIn={checkIn}
      />
    </>
  )
}
